<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { formatPlainDateByRegion } from '../../helpers';

const props = defineProps({
    post: { type: Object, required: true },
    hoverThreshold: { type: Number, default: 20 }
});

const router = useRouter();

const goToPost = () => {
    if (props.post?.slug?.name) {
        router.push(`/hkcmnews/${props.post.slug.name}`);
    }
};

const postImage = computed(() => {
    return props.post.image_lg || props.image || props.post.image || '';
});

const cardRef = ref(null);
const hoverActive = ref(false);

function handleMove(e) {
    const el = cardRef.value;

    if (!el) return;

    const r = el.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    const t = Math.max(0, props.hoverThreshold);

    hoverActive.value = x > t && x < r.width - t && y > t && y < r.height - t;
}

function handleLeave() {
    hoverActive.value = false;
}
</script>

<template>
    <div ref="cardRef" class="bg-white shadow-xl w-full h-[30rem] rounded-xl text-white relative cursor-pointer"
        @click="goToPost" @mousemove="handleMove" @mouseleave="handleLeave">
        <img :src="postImage" class="absolute inset-0 object-cover object-center w-full h-full rounded-xl"
            :alt="$translate(post.title)" />

        <div class="highlight-item-overlay hidden lg:block absolute top-0 bottom-0 p-2 bg-white w-0 z-10 transition-all ease-in-out duration-300 rounded-xl shadow-xl"
            :class="hoverActive ? 'opacity-100 lg:w-full' : 'opacity-0 lg:w-0'">
            <img :src="postImage" class="absolute inset-0 object-cover object-center w-full h-full rounded-xl"
                :alt="$translate(post.title)" />
            <div
                class="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-blue-dark/50 to-transparent rounded-t-xl">
            </div>

            <div v-if="post.user" class="absolute top-4 right-4 flex flex-row gap-2">
                <div class="text-right text-sm">
                    <p>{{ $t('author_male') }}:</p>
                    <strong>{{ post.user.name }}</strong>
                </div>
                <img v-if="post?.authorbox?.portrait" class="w-12 h-12 rounded-full object-cover bg-blue"
                    :src="post.authorbox.portrait" :alt="post.user.name" />
            </div>

            <div
                class="absolute bottom-0 left-0 right-0 p-4 min-h-[15rem] bg-gradient-to-t from-blue-dark/100 via-blue-dark/80 to-transparent rounded-b-xl">
                <div :class="hoverActive ? 'mt-20 opacity-100 delay-600 ease-linear' : 'mt-20 opacity-0'">
                    <div class="flex flex-row justify-between">
                        <div v-if="post.postCategories && post.postCategories.data.length" class="flex flex-row">
                            <div v-for="category in post.postCategories.data.slice(0, 5)" :key="category.id"
                                class="bg-blue text-white text-xs px-2 py-0.5 rounded-full">
                                {{ $translate(category.name) }}
                            </div>
                        </div>
                        <div>
                            <p>{{ formatPlainDateByRegion(post.published_at) }}</p>
                        </div>
                    </div>
                    <div class="mt-2">
                        <p class="font-bold">{{ $translate(post.title) }}</p>
                    </div>
                    <div class="mt-1 line-clamp-6">
                        <p>{{ $translate(post.teaser) }}</p>
                    </div>
                </div>
            </div>
        </div>

        <div
            class="absolute bottom-0 left-0 right-0 p-2 min-h-[15rem] bg-gradient-to-t from-blue-dark/90 via-blue-dark/50 to-transparent rounded-b-xl">
            <div class="mt-20">
                <div class="flex flex-row justify-between">
                    <div v-if="post.postCategories && post.postCategories.data.length" class="hidden 2xl:flex flex-row">
                        <div v-for="category in post.postCategories.data.slice(0, 5)" :key="category.id"
                            class="bg-blue text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                            {{ $translate(category.name) }}
                        </div>
                    </div>
                    <div>
                        <p class="line-clamp-4">{{ formatPlainDateByRegion(post.published_at) }}</p>
                    </div>
                </div>
                <div class="mt-2">
                    <p class="font-bold">{{ $translate(post.title) }}</p>
                </div>
            </div>
        </div>
    </div>
</template>
